import {
  AssetMediaRef,
  WorkflowRun,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

/**
 * Bound stimulus refs stamped on the run — same input seam as
 * `audienceIdFromRun` (`audienceFrom: input.audience`). Template key when
 * declared with `type === 'stimulus'`; else `stimulus`.
 *
 * Validates like `ThreadStimulusInput`'s parse: a JSON array of objects with
 * string `id` + `kind`. Junk entries are skipped.
 */
export function stimulusRefsFromRun(
  run: WorkflowRun,
  template: WorkflowTemplate | null,
): Array<AssetMediaRef> {
  const key =
    template?.inputs.find((spec) => spec.type === 'stimulus')?.key ?? 'stimulus'
  const raw = run.inputs[key]
  return parseStimulusInputValue(raw)
}

/**
 * Display / duplicate-check URL for a bound ref. Gate thumbs and the
 * media-compare Approve block use `publicUrl` then `url` only.
 */
export function stimulusPublicUrl(ref: AssetMediaRef): string | null {
  const candidate = ref.publicUrl ?? ref.url
  if (typeof candidate !== 'string') {
    return null
  }
  const trimmed = candidate.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Media-compare Approve block: N≥2 bound refs and fewer distinct public URLs
 * than refs (same file twice, or a missing URL). 1 ref + text versions is not
 * a media compare.
 */
export function hasDuplicateStimulusMedia(
  refs: Array<AssetMediaRef>,
): boolean {
  if (refs.length < 2) {
    return false
  }
  const urls = refs
    .map((ref) => stimulusPublicUrl(ref))
    .filter((url): url is string => url !== null)
  return new Set(urls).size < refs.length
}

/**
 * Version rows for a media compare: label = filename (or kind), content ===
 * label so spend / "Runs N times" stay honest when the list is the files.
 */
export function gateVersionsFromStimulusRefs(
  refs: Array<AssetMediaRef>,
): Array<{ label: string; content: string }> {
  return refs.map((ref) => {
    const label =
      typeof ref.label === 'string' && ref.label.trim().length > 0
        ? ref.label.trim()
        : ref.kind
    return { label, content: label }
  })
}

function parseStimulusInputValue(raw: unknown): Array<AssetMediaRef> {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return []
  }
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter(
      (entry): entry is AssetMediaRef =>
        typeof entry === 'object' &&
        entry !== null &&
        !Array.isArray(entry) &&
        typeof (entry as AssetMediaRef).id === 'string' &&
        typeof (entry as AssetMediaRef).kind === 'string',
    )
  } catch {
    return []
  }
}
